// Main entry point for the solar system simulator
import * as THREE from 'three';
import { Renderer3D } from './Renderer3D.js';
import { SimulationEngine } from './SimulationEngine.js';
import { UIController } from './UIController.js';
import { solarSystemData, getRealSizeRatios } from './solarSystemData.js';

console.log('Solar System 3D Simulator starting...');

let renderer3D = null;
let simulationEngine = null;
let uiController = null;
let lastUpdateTime = 0;
let animationFrameId = null; // id del bucle requestAnimationFrame (para poder cancelarlo)
let fpsMonitorInterval = null; // id del intervalo monitor de FPS (para poder limpiarlo)

// Delta máximo por frame (ms). Al volver de una pestaña en segundo plano, requestAnimationFrame
// se reanuda con un delta enorme; sin acotarlo la simulación daría un salto gigante y las estelas
// se dibujarían como líneas rectas. Lo limitamos a ~2 frames para que la animación siga suave.
const MAX_FRAME_DELTA = 50;

// Enhanced WebGL compatibility check with detailed error reporting
function checkWebGLSupport() {
    try {
        const canvas = document.createElement('canvas');
        const gl =
            canvas.getContext('webgl2') ||
            canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl');

        if (!gl) {
            return { supported: false, error: 'WebGL context not available' };
        }

        // Check for required extensions
        const requiredExtensions = ['OES_element_index_uint'];
        const missingExtensions = [];

        requiredExtensions.forEach((ext) => {
            if (!gl.getExtension(ext)) {
                missingExtensions.push(ext);
            }
        });

        // Check WebGL capabilities
        const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
        const maxVertexAttribs = gl.getParameter(gl.MAX_VERTEX_ATTRIBS);

        console.log('WebGL Info:', {
            version: gl.getParameter(gl.VERSION),
            vendor: gl.getParameter(gl.VENDOR),
            renderer: gl.getParameter(gl.RENDERER),
            maxTextureSize,
            maxVertexAttribs,
            missingExtensions,
        });

        return {
            supported: true,
            context: gl,
            capabilities: { maxTextureSize, maxVertexAttribs },
        };
    } catch (e) {
        return { supported: false, error: e.message };
    }
}

// Handle WebGL context loss
function setupWebGLErrorHandling(canvas) {
    canvas.addEventListener('webglcontextlost', (event) => {
        console.warn('WebGL context lost');
        event.preventDefault();
        showError(
            'WebGL context perdido',
            'La aplicación se pausará hasta que se restaure el contexto.'
        );
    });

    canvas.addEventListener('webglcontextrestored', () => {
        console.log('WebGL context restored');
        // Reinitialize the 3D scene
        if (renderer3D) {
            // Cancelar el bucle de animación y el monitor previos para no duplicarlos al reinicializar
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            if (fpsMonitorInterval !== null) {
                clearInterval(fpsMonitorInterval);
                fpsMonitorInterval = null;
            }
            if (uiController) {
                uiController.dispose();
            }
            renderer3D.dispose();
            initializeRenderer();
        }
    });
}

// Show error message to user
function showError(title, message, canRetry = false) {
    const loadingIndicator = document.getElementById('loading-indicator');
    const errorContainer = document.getElementById('error-container');

    loadingIndicator.classList.add('hidden');

    const retryButton = canRetry
        ? `
        <button onclick="location.reload()" style="
            background: #4CAF50;
            color: white;
            border: none;
            padding: 10px 20px;
            margin-top: 10px;
            border-radius: 5px;
            cursor: pointer;
        ">Reintentar</button>
    `
        : '';

    errorContainer.innerHTML = `
        <div class="error-message">
            <h3>${title}</h3>
            <p>${message}</p>
            ${retryButton}
        </div>
    `;
}

// Initialize the simulation engine
function initializeSimulation() {
    try {
        simulationEngine = new SimulationEngine(solarSystemData);
        console.log('Simulation engine initialized successfully');
        return true;
    } catch (error) {
        console.error('Error initializing simulation engine:', error);
        showError(
            'Error de simulación',
            `No se pudo inicializar el motor de simulación: ${error.message}`,
            true
        );
        return false;
    }
}

// Main animation loop with heliocentric simulation
function animate(currentTime) {
    animationFrameId = requestAnimationFrame(animate);

    // Calculate delta time, acotado para evitar el salto gigante al volver de segundo plano
    // (rAF se pausa en pestañas ocultas; ver MAX_FRAME_DELTA).
    let deltaTime = currentTime - lastUpdateTime;
    lastUpdateTime = currentTime;
    if (deltaTime > MAX_FRAME_DELTA) deltaTime = MAX_FRAME_DELTA;

    // Update simulation engine
    if (simulationEngine) {
        simulationEngine.update(deltaTime / 1000); // Convert to seconds

        // Get updated positions and rotations from simulation
        const planetPositions = simulationEngine.getAllPlanetPositions();
        const solarPosition = simulationEngine.getSolarPosition();
        const planetRotations = simulationEngine.getAllPlanetRotations();
        const simulationState = simulationEngine.getSimulationState();

        // Update renderer with heliocentric positions and rotations
        if (renderer3D) {
            renderer3D.updatePlanets(
                planetPositions,
                solarPosition,
                planetRotations,
                simulationState.isPaused,
                simulationState.currentTime
            );
            renderer3D.render();
        }
    }
}

// Initialize the 3D renderer
function initializeRenderer() {
    const canvas = document.getElementById('webgl-canvas');
    const loadingIndicator = document.getElementById('loading-indicator');
    const uiOverlay = document.getElementById('ui-overlay');

    try {
        // Create and initialize the 3D renderer
        renderer3D = new Renderer3D(canvas);
        renderer3D.initScene();

        // Initialize simulation engine
        if (!initializeSimulation()) {
            return; // Exit if simulation initialization failed
        }

        // Start the heliocentric animation loop
        lastUpdateTime = performance.now();
        animate(lastUpdateTime);

        // Setup resize handling
        window.addEventListener('resize', () => {
            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            renderer3D.resize(width, height);
        });

        // Initialize UI Controller
        uiController = new UIController();
        uiController.initialize(simulationEngine, renderer3D);
        uiController.startPeriodicUpdates();

        // Hide loading indicator and show UI toggle button
        loadingIndicator.classList.add('hidden');

        // Show UI toggle button (UI overlay starts closed)
        const uiToggleBtn = document.getElementById('ui-toggle-btn');
        if (uiToggleBtn) {
            uiToggleBtn.classList.remove('hidden');
        }

        // Keep UI overlay hidden initially
        uiOverlay.classList.add('hidden', 'collapsed');

        console.log('3D Scene initialized successfully');
        console.log('Three.js version:', THREE.REVISION);
        console.log('Heliocentric simulation active');

        // Monitor performance (guardamos el id para limpiarlo y evitar timers huérfanos)
        fpsMonitorInterval = setInterval(() => {
            const fps = renderer3D.getFPS();
            if (fps < 20) {
                console.warn(`Low FPS detected: ${fps}. Consider reducing quality.`);
            }
        }, 5000);
    } catch (error) {
        console.error('Error initializing 3D renderer:', error);
        showError(
            'Error de renderizado 3D',
            `No se pudo inicializar el renderizador 3D: ${error.message}`,
            true
        );
    }
}

// Initialize the application
function init() {
    const canvas = document.getElementById('webgl-canvas');

    if (!canvas) {
        console.error('Canvas element not found');
        showError('Error de configuración', 'No se encontró el elemento canvas necesario.');
        return;
    }

    // Check WebGL support with detailed diagnostics
    const webglCheck = checkWebGLSupport();

    if (!webglCheck.supported) {
        showError(
            'WebGL no soportado',
            `Tu navegador no soporta WebGL: ${webglCheck.error}. Por favor, actualiza tu navegador o usa uno compatible como Chrome, Firefox, Safari o Edge.`
        );
        return;
    }

    // Setup WebGL error handling
    setupWebGLErrorHandling(canvas);

    // Initialize the 3D renderer
    initializeRenderer();
}

// Expose simulation controls globally for UI integration
window.solarSimulator = {
    getSimulationEngine: () => simulationEngine,
    getRenderer3D: () => renderer3D,
    getUIController: () => uiController,

    // Simulation controls
    pauseSimulation: () => simulationEngine?.pause(),
    resumeSimulation: () => simulationEngine?.resume(),
    resetSimulation: () => simulationEngine?.reset(),
    setTimeScale: (scale) => simulationEngine?.setTimeScale(scale),
    getSimulationState: () => simulationEngine?.getSimulationState(),

    // Trail system controls
    setTrailsVisible: (visible) => renderer3D?.setTrailsVisible(visible),
    clearTrails: () => renderer3D?.clearTrails(),
    setTrailLength: (length) => renderer3D?.setTrailLength(length),
    getTrailStatistics: () => renderer3D?.getTrailStatistics(),
    getTrailSystem: () => renderer3D?.getTrailSystem(),
    setTrailType: (type) => renderer3D?.getTrailSystem()?.setTrailType(type),
    getTrailType: () => renderer3D?.getTrailSystem()?.getTrailType(),
    getAvailableTrailTypes: () => renderer3D?.getTrailSystem()?.getAvailableTrailTypes(),
    cycleTrailType: () => uiController?.cycleTrailType(),

    // Camera controls
    getCameraController: () => renderer3D?.getCameraController(),
    enableCameraControls: () => renderer3D?.enableCameraControls(),
    disableCameraControls: () => renderer3D?.disableCameraControls(),
    focusOnPlanet: (planetId, distance) => renderer3D?.focusOnPlanet(planetId, distance),
    resetCamera: () => renderer3D?.resetCamera(),
    applyCameraPreset: (preset) => renderer3D?.applyCameraPreset(preset),
    setCameraAutoRotate: (enabled, speed) => renderer3D?.setCameraAutoRotate(enabled, speed),
    getCameraState: () => renderer3D?.getCameraState(),

    // UI controls
    showError: (title, message) => uiController?.handleError(title, message),
    getUIState: () => uiController?.getUIState(),

    // Scale meter controls
    getScaleMeter: () => renderer3D?.getScaleMeter(),
    showScaleMeter: () => renderer3D?.showScaleMeter(),
    hideScaleMeter: () => renderer3D?.hideScaleMeter(),
    toggleScaleMeter: () => renderer3D?.toggleScaleMeter(),
    isScaleMeterVisible: () => renderer3D?.isScaleMeterVisible(),

    // UI visibility controls
    showUI: () => uiController?.showUI(),
    hideUI: () => uiController?.hideUI(),
    toggleUI: () => uiController?.toggleUIVisibility(),
    isUIVisible: () => uiController?.getUIState()?.visible,

    // POV mode controls
    togglePOVMode: (enabled) => uiController?.togglePOVMode(enabled),
    isPOVModeEnabled: () => renderer3D?.getCameraController()?.isPOVModeEnabled(),
    getPOVModeState: () => renderer3D?.getCameraController()?.getPOVModeState(),

    // Trail length controls
    setTrailLengthMode: (mode) => uiController?.setTrailLengthMode(mode),
    setTrailLengthMultiplier: (multiplier) => uiController?.setTrailLengthMultiplier(multiplier),
    getTrailLengthInfo: () => renderer3D?.getTrailSystem()?.getTrailLengthInfo(),
    debugTrailLengths: () => renderer3D?.getTrailSystem()?.debugTrailLengths(),

    // Realistic proportions info
    getRealSizeRatios: () => getRealSizeRatios(),
    isRealisticMode: () => true, // Always realistic mode

    // Planet info system controls
    getPlanetInfoSystem: () => renderer3D?.getPlanetInfoSystem(),
    enablePlanetInfo: () => renderer3D?.enablePlanetInfo(),
    disablePlanetInfo: () => renderer3D?.disablePlanetInfo(),
    getSelectedPlanet: () => renderer3D?.getSelectedPlanet(),
    selectPlanet: (planetId) => renderer3D?.selectPlanet(planetId),
    deselectPlanet: () => renderer3D?.deselectPlanet(),
};

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    // Cancelar el bucle de animación y los timers para evitar trabajo y fugas tras la descarga
    if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }
    if (fpsMonitorInterval !== null) {
        clearInterval(fpsMonitorInterval);
        fpsMonitorInterval = null;
    }
    if (uiController) {
        uiController.dispose();
    }
    if (renderer3D) {
        renderer3D.dispose();
    }
    if (simulationEngine) {
        simulationEngine.pause();
    }
});

// Start the application when DOM is loaded
document.addEventListener('DOMContentLoaded', init);
